import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProductDeleteDialogBox from './ProductDeleteDialogBox';
import ProductsFormModal from './ProductsFormModal';
import { useEditProductHandler } from '../dataHandlers/editProductHandler';
import { useDeleteProductHandler } from '../dataHandlers/deleteProductHandler';

const initialCurrentItemState = { item: null };

const ProductActions = ({ product }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(initialCurrentItemState);
  const [readOnly, setReadOnly] = useState(false);

  const { user } = useSelector((state) => state.org.org);
  const itemCodes = useSelector((state) => state.itemCodes.itemCodes);
  const uomCodes = useSelector((state) => state.uomCodes.uomCodes);
  const taxTypes = useSelector((state) => state.taxTypes.taxTypes);

  const itemCodesOptions = itemCodes.map((item) => ({ key: item.Code, value: item.Description }));
  const uomCodesOptions = uomCodes.map((item) => ({ key: item.Code, value: item.Name }));
  const taxTypesOptions = taxTypes.map((item) => ({ key: item.Code, value: item.Description }));

  const { onEdit } = useEditProductHandler();
  const { onDelete } = useDeleteProductHandler();

  const getTransformedProduct = (product) => {
    return {
      ...product,
      unit_of_measure: uomCodesOptions.find((type) => type.key === product.unit_of_measure),
      item_code: itemCodesOptions.find((type) => type.key === product.item_code),
      taxes: product?.taxes.map(({ category, ...rest }) => ({
        category: taxTypesOptions.find((type) => type.key === category),
        ...rest,
      })),
    };
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setReadOnly(false);
    setAnchorEl(null);
  };

  const handleDetails = () => {
    setReadOnly(true);
    setCurrentItem(getTransformedProduct(product));
    setOpen(true);
  };

  const handleEdit = () => {
    setReadOnly(false);
    setCurrentItem(getTransformedProduct(product));
    setOpen(true);
  };

  const handleDelete = async () => {
    setOpen(false);
    await onDelete(product);
    handleClose();
  };

  const handleEditSubmit = async (updatedProduct) => {
    await onEdit(updatedProduct);
    handleClose();
  };

  const hasPermission = user?.permissions?.products === 2 || user?.is_admin;

  return (
    <>
      <IconButton id="action-icon-button" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              boxShadow: '4px 4px 10px 0px rgba(0,0,0,0.2)',
            },
          },
        }}
      >
        <MenuItem id="details" onClick={handleDetails}>
          Details
        </MenuItem>
        {hasPermission && (
          <>
            <MenuItem id="edit" onClick={handleEdit}>
              Edit
            </MenuItem>
            <MenuItem id="delete" onClick={() => setCancelDialogOpen(true)}>
              Delete
            </MenuItem>
          </>
        )}
      </Menu>

      <ProductsFormModal
        open={open}
        handleClose={() => setOpen(false)}
        handleSubmit={handleEditSubmit}
        initialData={currentItem}
        readOnly={readOnly}
      />

      <ProductDeleteDialogBox
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onSubmit={handleDelete}
        product={product}
      />
    </>
  );
};

export default ProductActions;
